import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../app/store';
import { setTextLineOne, setTextLineTwo } from './napkinSlice';
import optionsConfig from './optionsConfig';
import './metallicEffects.css';
import { 
    shouldShowSingleLine, 
    calculateCharacterCount, 
    getMaxCharLimit,
    getFontConfig 
} from './fontBusinessRules';

interface ProductDisplayProps {
    // Define any props here if needed
}

const ProductDisplay: React.FC<ProductDisplayProps> = () => {
    const dispatch = useDispatch();
    const napkinState = useSelector((state: RootState) => state.napkin);
    const [charCountLineOne, setCharCountLineOne] = useState(0);
    const [charCountLineTwo, setCharCountLineTwo] = useState(0);

    // Update character counts when text or font changes
    useEffect(() => {
        setCharCountLineOne(calculateCharacterCount(napkinState.textLineOne));
        setCharCountLineTwo(calculateCharacterCount(napkinState.textLineTwo));
    }, [napkinState.textLineOne, napkinState.textLineTwo]);

    const handleLineOneChange = (e: React.FormEvent<HTMLDivElement>) => {
        const text = e.currentTarget.textContent || '';
        // Simple check - just dispatch if within reasonable length
        if (calculateCharacterCount(text) <= getMaxCharLimit(napkinState.font)) {
            dispatch(setTextLineOne(text));
        }
    };

    const handleLineTwoChange = (e: React.FormEvent<HTMLDivElement>) => {
        const text = e.currentTarget.textContent || '';
        
        // Check if second line should be hidden
        if (shouldShowSingleLine(napkinState.font, napkinState.image)) {
            return;
        }
        
        // Simple check - just dispatch if within reasonable length
        if (calculateCharacterCount(text) <= getMaxCharLimit(napkinState.font)) {
            dispatch(setTextLineTwo(text));
        }
    };

    // Get font configuration for dynamic sizing
    const getCurrentFontConfig = () => {
        return getFontConfig(napkinState.font) || optionsConfig.text.font[0];
    };

    // Check if second line should be hidden
    const hideSecondLine = shouldShowSingleLine(napkinState.font, napkinState.image);

    // Get text color from foil options
    const getCurrentTextColor = () => {
        const foilColor = optionsConfig.foil.color.find(c => c.name === napkinState.foilColor);
        return foilColor ? foilColor.hex : '#1C1C1C'; // Default to black
    };

    // Check if current color is metallic
    const isMetallic = () => {
        return napkinState.foilColor.toLowerCase().includes('metallic');
    };

    // Get metallic class name
    const getMetallicClass = () => {
        if (!isMetallic()) return '';
        
        const colorName = napkinState.foilColor.toLowerCase();
        if (colorName.includes('silver')) return 'metallic-silver';
        if (colorName.includes('gold')) return 'metallic-gold';
        if (colorName.includes('copper')) return 'metallic-copper';
        if (colorName.includes('red')) return 'metallic-red';
        if (colorName.includes('lilac')) return 'metallic-lilac';
        if (colorName.includes('magenta')) return 'metallic-magenta';
        if (colorName.includes('purple')) return 'metallic-purple';
        if (colorName.includes('blue')) return 'metallic-blue';
        if (colorName.includes('teal')) return 'metallic-teal';
        return '';
    };

    const fontConfig = getCurrentFontConfig();
    const textColor = getCurrentTextColor();

    // Dynamic class names based on napkin state
    const displayClasses = [
        'display-product',
        napkinState.color.toLowerCase()
    ].filter(Boolean).join(' ');
    
    // Separate rotation for just the napkin background
    const napkinWrapperClasses = napkinState.orientation === 'diamond' ? 'rotate marginsTopBottomCorrect' : '';

    const textStyle = {
        fontFamily: napkinState.font,
        fontSize: fontConfig.size || '20px',
        color: isMetallic() ? undefined : textColor, // Don't set color for metallic (gradient handles it)
        lineHeight: fontConfig.size || '20px'
    };

    // Text classes including metallic effects
    const textClasses = `embossed ${isMetallic() ? `metallic-text ${getMetallicClass()}` : ''}`;

    // Container needs to expand when diamond to accommodate rotation
    const containerStyle = {
        height: napkinState.orientation === 'diamond' ? '475px' : '350px',
        marginBottom: napkinState.orientation === 'diamond' ? '40px' : '20px',
        transition: 'height 0.4s ease-in-out, margin-bottom 0.4s ease-in-out'
    };

    // Content needs to shift down when diamond
    const contentStyle = {
        transform: napkinState.orientation === 'diamond' ? 'translateY(60px)' : 'translateY(0)',
        transition: 'transform 0.4s ease-in-out'
    };

    return (
        <div className="napkin-container" style={containerStyle}>
            {/* Napkin background that rotates */}
            <div className={`${displayClasses} ${napkinWrapperClasses}`}>
                <div className="napkin-ruffles"></div>
            </div>
            
            {/* Content that stays upright but moves with napkin */}
            <div className="napkin-content" style={contentStyle}>
                <div className="outer">
                    <div className="middle">
                        <div className="inner">
                            <div 
                                className={`imageContainer embossed ${isMetallic() ? 'metallic-icon-container' : ''}`}
                                style={{ color: isMetallic() ? undefined : textColor }}
                                data-image={napkinState.image}
                            >
                                {napkinState.image && napkinState.image !== 'a0' && (
                                    isMetallic() ? (
                                        <div 
                                            className={`metallic-icon ${getMetallicClass()}`}
                                            style={{
                                                WebkitMaskImage: `url(/images/${napkinState.image}.png)`,
                                                maskImage: `url(/images/${napkinState.image}.png)`,
                                            }}
                                        />
                                    ) : (
                                        <img 
                                            src={`/images/${napkinState.image}.png`}
                                            alt={napkinState.image}
                                            className="napkin-image regular-image-filter"
                                            data-color={napkinState.foilColor.toLowerCase().replace(/\s+/g, '')}
                                            onError={(e) => {
                                                // Fallback to local images if not found
                                                (e.target as HTMLImageElement).src = `/src/Images/${napkinState.image}.png`;
                                            }}
                                        />
                                    )
                                )}
                            </div>
                            <div
                                contentEditable
                                className={`text-line-one ${textClasses}`}
                                data-placeholder="Click to type"
                                onInput={handleLineOneChange}
                                suppressContentEditableWarning={true}
                                style={textStyle}
                                title={`${charCountLineOne}/${getMaxCharLimit(napkinState.font)} chars (caps count as 2)`}
                            ></div>
                            {!hideSecondLine && (
                                <div
                                    contentEditable
                                    className={`text-line-two ${textClasses}`}
                                    data-placeholder="Click to type"
                                    onInput={handleLineTwoChange}
                                    suppressContentEditableWarning={true}
                                    style={textStyle}
                                    title={`${charCountLineTwo}/${getMaxCharLimit(napkinState.font)} chars (caps count as 2)`}
                                ></div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductDisplay;
